import { useParams, useNavigate } from 'react-router-dom'
import { NavButton } from '../../components/NavButton'
import Person from '../../assets/person.svg?react'
import Document from '../../assets/document.svg?react'
import CheckmarkCircle from '../../assets/checkmark.circle.svg?react'
import XMarkCircle from '../../assets/xmark.circle.svg?react'
import Flag from '../../assets/flag.svg?react'
import Plus from '../../assets/plus.svg?react'
import Star from '../../assets/star.outline.svg?react'
import { NavBar } from '../../components/NavBar'
import { DashboardMenu } from '../../components/DashboardMenu'
import { ProfileView } from './ProfileView'
import { ApprovedSubmissionsView } from './approved/ApprovedSubmissionsView'
import { DeniedSubmissionsView } from './denied/DeniedSubmissionsView'
import { ReportsView } from './reports/ReportsView'
import { MyFragrancesView } from './MyFragrancesView'
import { MyReviewsView } from './MyReviewsView'
import { UnauthorizedAccess } from '../../components/UnauthorizedAccess'
import { useAuthStore } from '../../data/useAuthStore'
import { MetadataView } from './metadata/MetadataView'
import { SubmissionsView } from './submissions/SubmissionsView'

import { BlogPostsView } from './blog/BlogPostsView'

type NavItem =
  | 'profile'
  | 'submissions'
  | 'approved'
  | 'denied'
  | 'reports'
  | 'metadata'
  | 'blog'
  | 'myfragrances'
  | 'myreviews'

const validViews: NavItem[] = ['profile', 'submissions', 'approved', 'denied', 'reports', 'metadata', 'blog', 'myfragrances', 'myreviews']

export const DashboardPage = () => {
  const { view } = useParams<{ view?: string }>()
  const navigate = useNavigate()
  const isAdmin = useAuthStore((state) => state.isAdmin)
  const user = useAuthStore((state) => state.user)

  const activeView: NavItem = validViews.includes(view as NavItem) ? (view as NavItem) : 'profile'

  const handleNavigate = (newView: NavItem) => {
    navigate(`/dashboard/${newView}`)
  }

  // Block unauthenticated users
  if (!user) {
    return (
      <div className={'min-h-screen flex flex-col bg-background'}>
        <NavBar />
        <div className={'flex-1 flex items-center justify-center'}>
          <div className={'md:text-2xl text-xl text-center px-4'}>
            Please log in to access the dashboard
          </div>
        </div>
      </div>
    )
  }

  const renderContent = () => {
    switch (activeView) {
      case 'profile':
        return <ProfileView />
      case 'submissions':
        return isAdmin ? <SubmissionsView /> : <UnauthorizedAccess />
      case 'approved':
        return isAdmin ? <ApprovedSubmissionsView /> : <UnauthorizedAccess />
      case 'denied':
        return isAdmin ? <DeniedSubmissionsView /> : <UnauthorizedAccess />
      case 'reports':
        return isAdmin ? <ReportsView /> : <UnauthorizedAccess />
      case 'metadata':
        return isAdmin ? <MetadataView /> : <UnauthorizedAccess />
      case 'blog':
        return isAdmin ? <BlogPostsView /> : <UnauthorizedAccess />
      case 'myfragrances':
        return <MyFragrancesView />
      case 'myreviews':
        return <MyReviewsView />
      default:
        return <ProfileView />
    }
  }

  return (
    <div className="md:h-screen min-h-screen bg-background flex justify-center md:overflow-hidden">
      <NavBar
        dashboardMenu={
          <DashboardMenu
            isAdmin={isAdmin}
            activeView={activeView}
            onNavigate={handleNavigate}
          />
        }
      />
      <div className="flex w-full space-x-6 md:p-10 p-5 mt-20 box-border">
        {/* Sidebar — only as tall as its content, hidden on mobile */}
        <div className="hidden md:block">
          <section className="bg-white rounded-2xl w-64 p-5 border border-searchBorder flex-shrink-0">
            <NavButton
              icon={<Person width={18} height={18} />}
              label="Profile"
              active={activeView === 'profile'}
              onClick={() => handleNavigate('profile')}
              pillText={isAdmin ? 'Admin' : undefined}
              pillClassName="bg-yellow-300 text-yellow-900"
            />
            {isAdmin && (
              <>
                <NavButton
                  icon={<Document width={18} height={18} />}
                  label="Submissions"
                  active={activeView === 'submissions'}
                  onClick={() => handleNavigate('submissions')}
                />
                <NavButton
                  icon={<CheckmarkCircle width={18} height={18} />}
                  label="Approved submissions"
                  active={activeView === 'approved'}
                  onClick={() => handleNavigate('approved')}
                />
                <NavButton
                  icon={<XMarkCircle width={18} height={18} />}
                  label="Denied submissions"
                  active={activeView === 'denied'}
                  onClick={() => handleNavigate('denied')}
                />
                <NavButton
                  icon={<Flag width={18} height={18} />}
                  label="Reports"
                  active={activeView === 'reports'}
                  onClick={() => handleNavigate('reports')}
                />
                <NavButton
                  icon={<Document width={18} height={18} />}
                  label="Metadata"
                  active={activeView === 'metadata'}
                  onClick={() => handleNavigate('metadata')}
                />
                <NavButton
                  icon={<Document width={18} height={18} />}
                  label="Blog Posts"
                  active={activeView === 'blog'}
                  onClick={() => handleNavigate('blog')}
                />
              </>
            )}
            <NavButton
              icon={<Plus width={18} height={18} />}
              label="My fragrances"
              active={activeView === 'myfragrances'}
              onClick={() => handleNavigate('myfragrances')}
            />
            <NavButton
              icon={<Star width={18} height={18} />}
              label="My reviews"
              active={activeView === 'myreviews'}
              onClick={() => handleNavigate('myreviews')}
            />
          </section>
        </div>
        <section className="bg-white rounded-2xl flex-1 border border-searchBorder md:overflow-hidden pr-1">
          <div className="md:h-full md:overflow-y-auto scrollbar-hide-track">
            {renderContent()}
          </div>
        </section>
      </div>
    </div>
  )
}
